import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import bannerImg from "../../../assets/landing-page/alysei-bg.png";
import iconAi from "../../../assets/landing-page/icon-ai.png";
import useLocale from "../../../components/Hooks/useLocale";
import { urlWithLocalization } from "../../../components/LocalizationUrl/UrlWithLocalization";
import language from "../../../helpers/language";

const NewBanner = () => {
  const history = useHistory();
  const [lang] = useState(language.getLang());
  const localeLocalization = useLocale();
  const [search, setSearch] = useState("");
  const aiAssistantPlaceholders = useMemo(
    () => lang?.aiAssistantPlaceholders || ["What are you looking for today?"],
    [lang]
  );
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [typedPlaceholder, setTypedPlaceholder] = useState("");
  const [isDeletingPlaceholder, setIsDeletingPlaceholder] = useState(false);

  useEffect(() => {
    const currentText = aiAssistantPlaceholders[placeholderIndex] || "";

    if (
      !isDeletingPlaceholder &&
      typedPlaceholder.length === currentText.length
    ) {
      const pauseTimer = setTimeout(() => setIsDeletingPlaceholder(true), 1200);
      return () => clearTimeout(pauseTimer);
    }

    if (isDeletingPlaceholder && typedPlaceholder.length === 0) {
      setIsDeletingPlaceholder(false);
      setPlaceholderIndex(
        (currentIndex) => (currentIndex + 1) % aiAssistantPlaceholders.length
      );
      return undefined;
    }

    const typeTimer = setTimeout(
      () => {
        const nextLength =
          typedPlaceholder.length + (isDeletingPlaceholder ? -1 : 1);
        setTypedPlaceholder(currentText.slice(0, nextLength));
      },
      isDeletingPlaceholder ? 24 : 45
    );

    return () => clearTimeout(typeTimer);
  }, [
    aiAssistantPlaceholders,
    isDeletingPlaceholder,
    placeholderIndex,
    typedPlaceholder,
  ]);

  const focusAssistantSearch = () => {
    const input = document.getElementById("ai-assistant-search-input");
    input?.scrollIntoView({ behavior: "smooth", block: "center" });
    window.setTimeout(() => input?.focus(), 450);
  };

  return (
    <div className="relative min-h-screen flex flex-col justify-center pt-4 md:pt-20 overflow-hidden">
      {/* Background image */}
      <img
        src={bannerImg}
        alt="Italian landscape"
        fetchpriority="high"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          filter: "blur(3px)",
          transform: "scale(1.03)",
        }}
      />
      {/* Gradient overlay */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0) 50%, rgba(0,0,0,0.3) 100%)",
        }}
      />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center px-5 md:px-16 py-2 md:py-12">
        {/* Welcome pill */}
        <span className="inline-block bg-white/15 text-white text-xs font-medium px-4 py-1.5 rounded-md mb-6 backdrop-blur-md border border-white">
          {lang?.Welcome}
        </span>
        {/* Headline */}
        <h1
          className="text-white text-xl md:text-5xl lg:text-5xl font-bold leading-tight max-w-4xl mb-5"
          style={{ textShadow: "0 4px 12px rgba(0,0,0,0.3)" }}
        >
          {lang?.SourcePremium}
        </h1>
        {/* Sub-headline */}
        <p className="text-white/90 text-sm md:text-base max-w-2xl mb-7">
          {lang?.GetMatched}
        </p>

        {/* CTA Buttons */}
        <div className="flex gap-3 md:gap-4 justify-center mb-10">
          <button
            type="button"
            onClick={() => history.push("/chat")}
            className="px-3 md:px-12 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md transition-colors text-sm shadow-md"
          >
            {lang?.findProducers}
          </button>
          <Link
            to={urlWithLocalization(
              localeLocalization,
              "/register/italian-food-beverage-producers"
            )}
            className="join-as-seller learn-more-link  items-center justify-between inline-flex px-3 md:px-7 py-3 bg-royal hover:opacity-90 text-white hover:text-white font-semibold rounded-md transition-opacity text-sm shadow-md"
          >
            {lang?.join_as_producers}
            <span className="learn-more-link__icon w-7 h-7 rounded-full bg-white hidden md:flex items-center justify-center flex-shrink-0">
              <i className="fa fa-arrow-right text-royal text-xs" />
            </span>
          </Link>
        </div>
        {/* Search Card */}
        <div className="w-full max-w-2xl">
          <style>
            {`
              @keyframes alysei-gradient-border-spin {
                to {
                  transform: rotate(360deg);
                }
              }
            `}
          </style>
          <div
            className="relative rounded-2xl overflow-hidden shadow-2xl"
            style={{
              padding: "2px",
              boxShadow: "0 18px 36px rgba(0, 69, 119, 0.24)",
            }}
          >
            <div
              className="absolute"
              style={{
                inset: "-80%",
                background:
                  "conic-gradient(from 0deg, #14b8a6, #0ea5e9, #004577, #2dd4bf, #14b8a6)",
                animation: "alysei-gradient-border-spin 6s linear infinite",
              }}
            />
            <div
              id="ai-assistant-search"
              className="relative bg-white rounded-2xl p-5 text-left"
            >
            {/* Card header */}
            <div className="flex items-center gap-2 mb-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#0d9488"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5 flex-shrink-0"
              >
                <path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z" />
                <path d="M20 2v4" /><path d="M22 4h-4" />
                <circle cx="4" cy="20" r="2" />
              </svg>
              <h2 className="text-lg font-bold" style={{color: '#004577'}}>Hi I am Alysei AI Assistant</h2>
            </div>
            <p className="text-gray-500 text-sm mb-4">
              Describe your sourcing needs and we'll match you with relevant italian producers
            </p>

            {/* Input box */}
            <div className="border border-gray-200 rounded-xl px-4 py-3 flex items-start gap-3 focus-within:border-teal-400 focus-within:ring-2 focus-within:ring-teal-100 transition-all">
              <div className="flex-1 relative">
                {!search && (
                  <div className="absolute inset-0 flex items-center pointer-events-none text-sm text-gray-400">
                    <span>{typedPlaceholder || "What are you looking for today?"}</span>
                  </div>
                )}
                <input
                  id="ai-assistant-search-input"
                  type="text"
                  placeholder=""
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && search.trim()) {
                      history.push("/chat", { initialMessage: search.trim() });
                    }
                  }}
                  className="w-full text-sm text-gray-800 outline-none bg-transparent relative z-10"
                  style={{ caretColor: "auto" }}
                />
              </div>
              <button
                onClick={() => {
                  if (search.trim()) {
                    history.push("/chat", { initialMessage: search.trim() });
                  }
                }}
                className="w-7 h-7 rounded-full text-white flex items-center justify-center flex-shrink-0 bg-green-600 hover:bg-green-700"
                style={{  transition: 'background-color 0.2s' }}
                // onMouseEnter={e => e.currentTarget.style.backgroundColor = '#0d9488'}
                // onMouseLeave={e => e.currentTarget.style.backgroundColor = '#14b8a6'}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="19" x2="12" y2="5" />
                  <polyline points="5 12 12 5 19 12" />
                </svg>
              </button>
            </div>

            {/* Quick reply chips */}
            <div className="flex flex-wrap gap-2 mt-4">
              {[
                "🍷 Wine & Beverages",
                "🍲 Food",
                "🏭 Private Label",
                "📍 Regional Specialties",
              ].map((chip) => (
                <button
                  key={chip}
                  onClick={() => history.push("/chat", { initialMessage: chip })}
                  className="text-xs rounded-full px-3 py-1 bg-white"
                  style={{ color: '#374151', border: '1px solid #d1d5db', transition: 'border-color 0.2s, color 0.2s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#2dd4bf'; e.currentTarget.style.color = '#0d9488'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#374151'; }}
                >
                  {chip}
                </button>
              ))}
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewBanner;
